import { useTranslation } from 'react-i18next';
import { Ban, CheckCircle, Circle, Hourglass, Lock, Pause, ShieldCheck, Siren } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TicketStatus, ticketStatusLabelKey } from '@nbh/shared';

/**
 * Status is distinguished by border treatment and icon, not colour alone: solid,
 * dashed, filled and greyed read differently even on a washed-out screen in a
 * plant room (SPEC.md §5.1.2 UI note).
 */

interface StatusStyle {
  Icon: LucideIcon;
  /** Border and fill treatment — the primary non-colour signal. */
  frame: string;
}

const STYLES: Record<TicketStatus, StatusStyle> = {
  [TicketStatus.Open]: {
    Icon: Circle,
    frame: 'border-4 border-solid border-red-400 text-red-100 bg-red-950/50',
  },
  [TicketStatus.Escalated]: {
    Icon: Siren,
    // Pulse, not a hard flash — index.css disables it under prefers-reduced-motion.
    frame: 'border-4 border-double border-red-300 text-red-50 bg-red-900 animate-pulse',
  },
  [TicketStatus.InProgress]: {
    Icon: Hourglass,
    frame: 'border-2 border-solid border-amber-400 text-amber-100 bg-amber-950/50',
  },
  [TicketStatus.OnHold]: {
    Icon: Pause,
    frame: 'border-2 border-dashed border-sky-400 text-sky-100 bg-sky-950/50',
  },
  [TicketStatus.Resolved]: {
    Icon: CheckCircle,
    frame: 'border-2 border-solid border-emerald-400 text-emerald-950 bg-emerald-400',
  },
  [TicketStatus.Closed]: {
    Icon: Lock,
    frame: 'border-2 border-solid border-slate-600 text-slate-400 bg-transparent',
  },
  // Struck through, and a different icon from CLOSED. Both are finished, but only one
  // was done — a cancelled job must not read at a glance as a completed one (D27).
  [TicketStatus.Cancelled]: {
    Icon: Ban,
    frame: 'border-2 border-dotted border-slate-600 text-slate-500 bg-transparent line-through',
  },
  // Green like RESOLVED, because the fault is still fixed, but doubled border and a shield
  // rather than a tick: the difference between "the technician says it is done" and "a
  // manager has been and checked" has to survive a washed-out screen in a plant room.
  [TicketStatus.Verified]: {
    Icon: ShieldCheck,
    frame: 'border-4 border-double border-emerald-300 text-emerald-950 bg-emerald-400',
  },
};

export interface StatusBadgeProps {
  status: TicketStatus;
  /** Shown only for ON_HOLD — why the job is paused is the point of the state. */
  holdReason?: string | null;
}

export function StatusBadge({ status, holdReason }: StatusBadgeProps) {
  const { t } = useTranslation();
  const { Icon, frame } = STYLES[status];
  const showReason = status === TicketStatus.OnHold && Boolean(holdReason?.trim());

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold ${frame}`}
        data-status={status}
      >
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        <span>{t(ticketStatusLabelKey[status])}</span>
      </span>

      {showReason && (
        <span className="text-xs text-sky-200/90 italic" data-testid="hold-reason">
          {holdReason}
        </span>
      )}
    </span>
  );
}
